import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary component that catches JavaScript errors
 * anywhere in the child component tree and displays a fallback UI.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details for debugging
    console.error("ErrorBoundary caught an error:", {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });
  }

  handleRefresh = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI with Eunice styling
      return (
        <div className="min-h-screen bg-cream flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center space-y-6">
            {/* Error icon */}
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-primary" />
            </div>

            {/* Heading */}
            <div className="space-y-2">
              <h1 className="font-serif text-2xl md:text-3xl text-foreground">
                Something went wrong
              </h1>
              <p className="text-muted-foreground font-sans leading-relaxed">
                We encountered an unexpected error. Don't worry—your data is safe. 
                Please try refreshing the page.
              </p>
            </div>

            {/* Actions */}
            <div className="space-y-4">
              <Button
                onClick={this.handleRefresh}
                variant="eunice"
                size="lg"
                className="w-full sm:w-auto"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Page
              </Button>

              <p className="text-sm text-muted-foreground font-sans">
                If the problem persists, please{" "}
                <a
                  href="mailto:letters@eunice.world"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  <Mail className="h-3 w-3" />
                  contact support
                </a>
              </p>
            </div>

            {/* Error details for development */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="mt-8 text-left bg-background/50 rounded-lg p-4 border border-tan/30">
                <summary className="cursor-pointer text-sm font-sans text-muted-foreground hover:text-foreground">
                  Technical details
                </summary>
                <pre className="mt-3 text-xs text-destructive overflow-auto whitespace-pre-wrap font-mono">
                  {this.state.error.message}
                  {"\n\n"}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
